import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil, faUpload } from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Button, LoadingBubbles, Text,
} from '@domain-group/suit-theme'

import useArtwork from '@hooks/useArtwork'

import ArtworkPagePreview from '@components/ArtworkPagePreview'
import type { ArtworkPagePreviewRenderParams } from '@components/ArtworkPagePreview'

import type { ArtworkModel } from '@models/types'

import ReviewStatus from './ReviewStatus'

interface EditArtworkProps {
  boxProps?: object,
  itemPayload: {
    artwork?: ArtworkModel,
    callbacks: {
      showUploadArtworkModal?: VoidFunction,
    },
  },
  redirectUrl?: string,
}

const EditArtwork = (props: EditArtworkProps) => {
  const {
    boxProps,
    itemPayload: {
      artwork,
      callbacks: {
        showUploadArtworkModal,
      },
    },
    redirectUrl,
  } = props

  const artworkPayload = useArtwork(artwork, {
    generateUrlsOptions: {
      includeRedirect: true,
      redirectUrl,
    },
  })

  const {
    artwork: { id },
    isApproved,
    isUploadedArtwork,
    selectedArtworkPage,
    urls: {
      editArtworkUrl,
    },
  } = artworkPayload

  return (
    <Box
      alignItems="center"
      backgroundColor="white"
      borderRadius={5}
      border="1px solid"
      borderColor="lineColor"
      justifyContent="space-between"
      marginTop="medium"
      {...boxProps}
    >
      <Box
        height={100}
        borderTopLeftRadius={5}
        borderBottomLeftRadius={5}
        borderRight="1px solid"
        borderColor="lineColor"
        backgroundColor="whiteGrey"
        flexShrink="0"
        width={100}
      >
        <ArtworkPagePreview
          // @ts-expect-error TS2739
          artworkPage={selectedArtworkPage}
          configOptions={{
            ablyPublicKey: process.env.REACT_APP_ABLY_ARTWORK_BUILDER_PUBLIC_KEY || '',
          }}
          height={98}
          width={98}
        >
          {(params: ArtworkPagePreviewRenderParams) => {
            const { buildingRef } = params

            // Loading
            if (buildingRef.current){
              return (
                <LoadingBubbles color="bodyFontLightColor" />
              )
            }

            // Approved
            if (!buildingRef.current && isApproved){
              return (
                <Box
                  alignItems="center"
                  backgroundColor="rgba(67, 172, 106, 0.8)"
                  borderTopLeftRadius={5}
                  borderBottomLeftRadius={5}
                  color="white"
                  flexDirection="column"
                  height="100%"
                  justifyContent="center"
                  width="100%"
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <Text color="white" fontSize="xsmall" lineHeight="1.3" marginTop="medium" textAlign="center">
                    Artwork Approved
                  </Text>
                </Box>
              )
            }

            return null
          }}
        </ArtworkPagePreview>
      </Box>

      <Box flexDirection={['column', 'row']}>
        <Box flexDirection="column" marginLeft="large" width="auto">
          <Text fontSize="small">{`Artwork #${id}`}</Text>
          <ReviewStatus artworkPayload={artworkPayload} />
        </Box>

        <Box marginLeft={['large', 'auto']} marginRight={[0, 'large']} marginTop={['large', 0]} width="auto">
          {!isApproved && isUploadedArtwork && (
            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faUpload} />}
              onClick={showUploadArtworkModal}
              size="small"
              // @ts-expect-error
              width="auto"
            >
              Change Artwork
            </Button>
          )}

          {!isApproved && !isUploadedArtwork && (
            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="secondaryEdit"
              href={editArtworkUrl}
              icon={<FontAwesomeIcon icon={faPencil} />}
              size="small"
              width="auto"
            >
              Edit Artwork
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default EditArtwork
